import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import * as Icons from "lucide-react";
import { processSteps, images } from "../mock";

export const Process = () => {
  const logistics = [
    {
      title: "Deutschlandweite Abholung",
      description: "Wir holen Ihre Geräte kostenfrei an jedem Standort in Deutschland ab",
      icon: "MapPin",
    },
    {
      title: "Versicherte Transporte",
      description: "Alle Transporte sind vollständig versichert für Ihre Sicherheit",
      icon: "Shield",
    },
    {
      title: "Flexible Terminplanung",
      description: "Abholung nach Ihrem Zeitplan – schnell und unkompliziert",
      icon: "Calendar",
    },
    {
      title: "Professionelle Verpackung",
      description: "Auf Wunsch stellen wir Verpackungsmaterial zur Verfügung",
      icon: "Package",
    },
  ];

  const timeline = [
    {
      phase: "Tag 1-2",
      title: "Anfrage & Bewertung",
      description: "Nach Ihrer Anfrage erfolgt die Bewertung Ihrer Geräte",
    },
    {
      phase: "Tag 3-4",
      title: "Angebot & Zusage",
      description: "Sie erhalten unser Ankaufsangebot und stimmen zu",
    },
    {
      phase: "Tag 5-7",
      title: "Abholung",
      description: "Termingerechte Abholung an Ihrem Standort",
    },
    {
      phase: "Tag 8-10",
      title: "Prüfung & Datenlöschung",
      description: "Geräteprüfung und zertifizierte Datenlöschung",
    },
    {
      phase: "Tag 11-12",
      title: "Auszahlung",
      description: "Schnelle Auszahlung nach erfolgter Prüfung",
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-blue-50 py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              Ablauf & Logistik
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Unser professioneller Ankaufsprozess ist transparent, sicher und unkompliziert. 
              Von der ersten Anfrage bis zur Auszahlung – wir begleiten Sie durch jeden Schritt.
            </p>
          </div>
        </div>
      </section>

      {/* Process Steps Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              5-Schritte-Prozess
            </h2>
            <p className="text-xl text-gray-600">
              Einfach, sicher und transparent
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            {processSteps.map((step, index) => (
              <div key={index} className="flex gap-6 mb-12 last:mb-0">
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 bg-blue-600 text-white rounded-full flex items-center justify-center text-3xl font-bold shadow-lg">
                    {step.step}
                  </div>
                </div>
                <div className="flex-1 pt-4">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <img
              src={images.businessMeeting}
              alt="Business Process"
              className="w-full h-96 object-cover rounded-2xl shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* Logistics Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Logistik-Services
            </h2>
            <p className="text-xl text-gray-600">
              Professionelle Abholung und Transport
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {logistics.map((item, index) => {
              const Icon = Icons[item.icon];
              return (
                <Card key={index} className="border-blue-100 hover:border-blue-300 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="text-blue-600" size={28} />
                    </div>
                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 text-lg">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 bg-blue-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Zeitlicher Ablauf
            </h2>
            <p className="text-xl text-gray-600">
              Von der Anfrage bis zur Auszahlung in ca. 12 Tagen
            </p>
          </div>
          <div className="max-w-4xl mx-auto space-y-4">
            {timeline.map((item, index) => (
              <Card key={index} className="border-blue-200 hover:shadow-md transition-all duration-300">
                <CardContent className="p-6 flex items-center space-x-6">
                  <div className="flex-shrink-0">
                    <div className="w-20 h-20 bg-blue-600 text-white rounded-lg flex items-center justify-center">
                      <span className="text-sm font-bold text-center">{item.phase}</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-600">
                      {item.description}
                    </p>
                  </div>
                  <Icons.CheckCircle2 className="text-blue-600 flex-shrink-0" size={24} />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
              Häufige Fragen
            </h2>
            <div className="space-y-6">
              {[
                {
                  q: "Wie schnell erfolgt die Abholung?",
                  a: "In der Regel innerhalb von 5-7 Werktagen nach Angebotsannahme, bei Bedarf auch schneller.",
                },
                {
                  q: "Entstehen Kosten für die Abholung?",
                  a: "Nein, die Abholung ist für Sie vollständig kostenfrei.",
                },
                {
                  q: "Was muss ich vor der Abholung tun?",
                  a: "Idealerweise sollten die Geräte verpackt und für den Transport bereit sein. Wir unterstützen Sie aber auch bei der Verpackung.",
                },
                {
                  q: "Wann erfolgt die Auszahlung?",
                  a: "Die Auszahlung erfolgt nach erfolgter Prüfung und Datenlöschung, in der Regel innerhalb von 12 Tagen nach Anfrage.",
                },
              ].map((faq, index) => (
                <Card key={index} className="border-blue-100 hover:shadow-md transition-all duration-300">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {faq.q}
                    </h3>
                    <p className="text-gray-600">
                      {faq.a}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Bereit für den Start?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Starten Sie jetzt Ihren unkomplizierten Ankaufsprozess
            </p>
            <Link to="/request-quote">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 font-medium shadow-lg">
                Jetzt Anfrage stellen
                <Icons.ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
